import { useMemo, useState, type ReactNode } from 'react'
import { addMonths, endOfMonth, format, startOfMonth, subMonths } from 'date-fns'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import {
  CALENDAR_COLOR_CLASSES,
  MonthGrid,
  STAY_EDGE_INSET,
  getCalendarColorKey,
  parseStay,
  type CalendarReservation,
  type MonthBar,
} from '@/components/calendar/month-grid'
import { STAY_FOCUS_RING } from '@/components/calendar/stay-bars'
import { ChannelLegend } from '@/components/calendar/legend'
import { dateToMonthParam, monthParamToDate } from '@/lib/calendar-month'
import { getChannelLabel, formatCurrency } from '@/lib/format-utils'
import { isReservationCancelled } from '@/lib/reservation-utils'
import { usePropertyPricingCalendar } from '@/hooks/useApi'
import { cn } from '@/lib/utils'

/**
 * Calendar tab for the property detail page.
 *
 * Renders a Monday-start month grid with reservation stays drawn as
 * continuous bars spanning check-in to check-out (rendered as one block per
 * stay, not per-day fragments). The grid itself - week splitting, lane
 * packing, row heights - is `MonthGrid`, shared with the cleaning board's
 * context calendar; what lives here is what a bar MEANS for this screen.
 *
 * Mostly presentational - `reservations` is still fed in as a prop (whatever
 * `usePropertyReservations` already returns) - but per-night pricing is
 * fetched internally via usePropertyPricingCalendar, since it's inherently
 * scoped to whichever month is currently visible (a piece of state only this
 * component owns), unlike `reservations` which covers every month up front.
 * Best-effort: a property with no PriceLabs listing mapped simply shows no
 * prices, not an error (see usePropertyPricingCalendar's own docs).
 */

interface Stay {
  reservation: CalendarReservation
  checkInDate: Date
  checkOutDate: Date
}

function getCalendarBarClasses(reservation: CalendarReservation) {
  return CALENDAR_COLOR_CLASSES[getCalendarColorKey(reservation)]
}

function getReservationId(reservation: CalendarReservation) {
  return reservation.id || reservation.reservation_id
}

/**
 * Whether any stay in `staysInMonth` covers this calendar night - used to
 * decide whether a day cell shows a per-night price (unbooked days only).
 */
function isNightBooked(day: Date, staysInMonth: Stay[]) {
  return staysInMonth.some(
    ({ checkInDate, checkOutDate }) => day >= checkInDate && day < checkOutDate
  )
}

interface CalendarTabProps {
  propertyId: string
  reservations?: CalendarReservation[]
  onSelectReservation?: (reservation: CalendarReservation) => void
  needsReplyReservationIds?: Set<string>
  /**
   * The month shown, as `YYYY-MM`, when the caller owns it (the property
   * Calendar tab keeps it in the URL). Left out, the tab pages itself.
   */
  month?: string
  onMonthChange?: (month: string) => void
  /** Adds a "Today" button that jumps back to the current month. */
  showToday?: boolean
  /** Bars the caller draws beside the stays - blocks, on the property Calendar tab. */
  extraBars?: MonthBar[]
  /** Legend entries for `extraBars`, after the channel colours. */
  extraLegend?: ReactNode
  /** Rendered between the title row and the legend: layer toggles, say. */
  controls?: ReactNode
  /** The reservation whose bar is ringed, because its sheet is open. */
  focusedReservationId?: string | null
  /** No data has arrived yet: the grid shows, but "no reservations" is not claimed. */
  isLoading?: boolean
  /**
   * Whether an empty grid means an empty month. The caller turns this off when
   * a layer it is drawing failed or is switched off: bars that are missing
   * because the data is unavailable must not read as "nothing booked".
   */
  canClaimEmpty?: boolean
  /**
   * The bars are for another month still: kept on screen, dimmed and inert (no
   * bar, block or day number opens anything), until this one lands.
   */
  isStale?: boolean
  /**
   * Whether anything the grid shows is still on its way. Left out, it is
   * `isLoading || isStale`; the caller knows about other reads (the crews).
   */
  busy?: boolean
  /**
   * Shown in place of the legend, the grid and the empty-month line when the
   * month could not be read at all. The title and month paging stay, so the
   * operator can page away from a month that will not load.
   */
  error?: ReactNode
  /** Least height of a week row in px (see `MonthGrid`); the property Calendar tab sets it. */
  dayMinHeight?: number
  /** Markers along the bottom of each day cell, under every bar (see `MonthGrid`). */
  renderDayFooter?: (day: Date) => ReactNode
  /** Makes each day number a button that calls this (see `MonthGrid`). */
  onSelectDay?: (day: Date) => void
  /** Drag-to-select a range of days (see `MonthGrid`); off while the grid is stale. */
  onDragSelect?: (first: Date, last: Date) => void
  /**
   * The month as a list, for a screen too narrow for the grid: drawn in place
   * of the legend, the grid and the empty-month line (which the list says for
   * itself). The title, month paging and `controls` stay.
   */
  agenda?: ReactNode
}

/**
 * `needsReplyReservationIds` is a best-effort Set of reservation ids whose
 * thread currently needs a reply, derived by the caller from whatever
 * conversation-list pages are already loaded (property-messages.jsx) - not
 * a fresh fetch of its own. Like the conversation list's own "Needs reply"
 * filter, this is bounded by what's currently paginated in, not exhaustive.
 */
export function CalendarTab({
  propertyId,
  reservations = [],
  onSelectReservation,
  needsReplyReservationIds,
  month,
  onMonthChange,
  showToday = false,
  extraBars,
  extraLegend,
  controls,
  focusedReservationId = null,
  canClaimEmpty = true,
  isLoading = false,
  isStale = false,
  busy,
  error,
  dayMinHeight,
  renderDayFooter,
  onSelectDay,
  onDragSelect,
  agenda,
}: CalendarTabProps) {
  const [ownMonthCursor, setOwnMonthCursor] = useState(() => startOfMonth(new Date()))
  const monthCursor = useMemo(
    () => (month ? monthParamToDate(month) : ownMonthCursor),
    [month, ownMonthCursor]
  )
  const goToMonth = (next: Date) => {
    setOwnMonthCursor(next)
    onMonthChange?.(dateToMonthParam(next))
  }

  // The agenda has no nightly prices, so it does not ask for them.
  const pricingQuery = usePropertyPricingCalendar(agenda ? null : propertyId, {
    year: monthCursor.getFullYear(),
    month: monthCursor.getMonth() + 1,
  })
  const priceByDate = useMemo(() => {
    const map = new Map<string, number>()
    for (const day of pricingQuery.data?.days ?? []) {
      if (day?.date && typeof day.price === 'number') map.set(day.date, day.price)
    }
    return map
  }, [pricingQuery.data])

  const stays = useMemo<Stay[]>(() => {
    return reservations
      .filter(reservation => !isReservationCancelled(reservation))
      .map(reservation => {
        const parsed = parseStay(reservation)
        if (!parsed) return null
        return { reservation, ...parsed }
      })
      .filter((stay): stay is Stay => Boolean(stay))
  }, [reservations])

  const monthInterval = useMemo(
    () => ({ start: startOfMonth(monthCursor), end: endOfMonth(monthCursor) }),
    [monthCursor]
  )

  const staysInMonth = useMemo(() => {
    // Touches the visible month if its check-out morning is on/after the month
    // start and its first night is on/before the month end: a stay checking
    // out on the 1st still draws its tail into that cell (the grid makes the
    // same call).
    return stays.filter(
      ({ checkInDate, checkOutDate }) =>
        checkOutDate >= monthInterval.start && checkInDate <= monthInterval.end
    )
  }, [stays, monthInterval])

  // A stay that only checks out on the 1st draws its tail, but books no night
  // here: the month can still say it has no reservations.
  const hasNightInMonth = useMemo(
    () => staysInMonth.some(({ checkOutDate }) => checkOutDate > monthInterval.start),
    [staysInMonth, monthInterval]
  )

  const handleSelect = (reservation: CalendarReservation) => {
    if (onSelectReservation) {
      onSelectReservation(reservation)
    } else {
      console.log('CalendarReservation selected', getReservationId(reservation))
    }
  }

  const bars = useMemo<MonthBar[]>(() => {
    return staysInMonth.map(({ reservation, checkInDate, checkOutDate }) => {
      const reservationId = getReservationId(reservation)
      const guestName = reservation.guest_name?.trim() || 'Guest'
      const totalPayout = reservation.total_payout ?? reservation.external?.uplisting?.total_payout
      const hasPayout = totalPayout !== null && totalPayout !== undefined
      const payoutSuffix = hasPayout ? ` · ${formatCurrency(totalPayout)}` : ''
      const barLabel = `${guestName}${payoutSuffix}`
      const needsReply = needsReplyReservationIds?.has(reservationId ?? '') ?? false
      const needsReplyDot = needsReply && (
        <span
          className="mr-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-current align-middle"
          aria-hidden="true"
        />
      )
      return {
        key: String(reservationId),
        start: checkInDate,
        endExclusive: checkOutDate,
        edgeInset: STAY_EDGE_INSET,
        continuesPastMonth: true,
        className: cn(
          getCalendarBarClasses(reservation),
          focusedReservationId === String(reservationId) && STAY_FOCUS_RING
        ),
        testId: 'reservation-bar',
        data: {
          'data-channel': reservation.channel || 'unknown',
          'data-color-key': getCalendarColorKey(reservation),
        },
        title: barLabel,
        ariaLabel: `${barLabel} reservation via ${getChannelLabel(reservation.channel)}${
          needsReply ? ' - needs reply' : ''
        }`,
        onClick: () => handleSelect(reservation),
        label: (
          <>
            {needsReplyDot}
            {barLabel}
          </>
        ),
        // A one-night stay is half a cell wide: the first name alone, so it
        // reads as a name rather than "E...".
        shortLabel: (
          <>
            {needsReplyDot}
            {guestName.split(/\s+/)[0]}
          </>
        ),
      }
    })
    // `handleSelect` is re-created every render and depends only on the
    // prop below, so it is deliberately not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staysInMonth, needsReplyReservationIds, onSelectReservation, focusedReservationId])

  // A stale bar belongs to the month before: it must not open anything.
  const allBars = useMemo(() => {
    const all = [...bars, ...(extraBars ?? [])]
    return isStale ? all.map(({ onClick: _onClick, ...bar }) => bar) : all
  }, [bars, extraBars, isStale])

  // "unknown" is a fallback bucket, listed only when a stay this month is in it.
  const hasUnknownStay = useMemo(
    () => staysInMonth.some(({ reservation }) => getCalendarColorKey(reservation) === 'unknown'),
    [staysInMonth]
  )

  const goToPreviousMonth = () => goToMonth(subMonths(monthCursor, 1))
  const goToNextMonth = () => goToMonth(addMonths(monthCursor, 1))
  const goToToday = () => goToMonth(startOfMonth(new Date()))

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-x-4 gap-y-3">
        <CardTitle className="whitespace-nowrap">{format(monthCursor, 'MMMM yyyy')}</CardTitle>
        <div className="ml-auto flex items-center gap-2">
          {showToday && (
            <Button type="button" variant="outline" size="sm" onClick={goToToday}>
              Today
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Previous month"
            onClick={goToPreviousMonth}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Next month"
            onClick={goToNextMonth}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          error
        ) : (
          <>
            {controls}
            {agenda ? (
              agenda
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-3 text-xs text-textMuted">
                  <ChannelLegend withUnknown={hasUnknownStay} />
                  {extraLegend}
                </div>

                <div
                  aria-busy={busy ?? (isLoading || isStale)}
                  data-stale={isStale ? 'true' : undefined}
                  // `inert` takes the stale grid out of the tab order and stops every
                  // click on it, whatever it contains (event blocks included).
                  inert={isStale}
                  className={cn('transition-opacity', isStale && 'select-none opacity-60')}
                >
                  <MonthGrid
                    monthCursor={monthCursor}
                    bars={allBars}
                    dayMinHeight={dayMinHeight}
                    renderDayFooter={renderDayFooter}
                    onSelectDay={isStale ? undefined : onSelectDay}
                    onDragSelect={isStale ? undefined : onDragSelect}
                    renderDayCorner={day => {
                      const price = isNightBooked(day, staysInMonth)
                        ? undefined
                        : priceByDate.get(format(day, 'yyyy-MM-dd'))
                      if (price === undefined) return null
                      return (
                        <span
                          className="truncate text-[10px] text-textFaint"
                          data-testid="calendar-day-price"
                        >
                          {formatCurrency(price, pricingQuery.data?.currency)}
                        </span>
                      )
                    }}
                  />
                </div>

                {canClaimEmpty &&
                  !isLoading &&
                  !isStale &&
                  !hasNightInMonth &&
                  (extraBars?.length ?? 0) === 0 && (
                    <EmptyState message="No reservations for this month" />
                  )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
